import React, { useState } from 'react';
import { Copy, Check, RefreshCw, BarChart3, Pencil, GitBranch } from 'lucide-react';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { applyAlpha } from '../lib/colorUtils';

/**
 * MessageActions component displays action buttons below messages
 * @param {Object} props
 * @param {Object} props.message - The message object
 * @param {'user' | 'assistant'} props.role - The role of the message sender
 * @param {Function} props.onCopy - Callback when copy is clicked
 * @param {Function} props.onRegenerate - Callback when regenerate is clicked (assistant only)
 * @param {Function} props.onEdit - Callback when edit is clicked (user only)
 * @param {Function} props.onBranch - Callback when branch is clicked (creates new conversation branch from this message)
 * @param {Function} props.onTestAudience - Callback when test audience is clicked
 * @param {boolean} props.showTestAudience - Whether to show the test audience button
 * @param {Object} props.brandVisualTheme - Brand visual theme for styling
 * @param {boolean} props.isStreaming - Whether we're currently streaming
 */
const MessageActions = ({
  message,
  role,
  onCopy,
  onRegenerate,
  onEdit,
  onBranch,
  onTestAudience,
  showTestAudience = false,
  brandVisualTheme,
  isStreaming = false,
}) => {
  const [copied, setCopied] = useState(false);

  const palette = brandVisualTheme?.palette || {};
  const primaryColor = palette.primary || '#4f46e5';
  const primaryLight = applyAlpha(primaryColor, 0.1);
  const primaryBorder = applyAlpha(primaryColor, 0.3);

  const handleCopy = async () => {
    if (!message?.content) return;
    
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      onCopy?.();
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleRegenerate = () => {
    if (isStreaming) return;
    onRegenerate?.(message);
  };

  const handleEdit = () => {
    if (isStreaming) return;
    onEdit?.(message);
  };

  const handleTestAudience = () => {
    if (isStreaming) return;
    onTestAudience?.(message);
  };

  const handleBranch = () => {
    if (isStreaming) return;
    onBranch?.(message);
  };

  // Unified button styling: no background on hover, just color change
  const buttonBaseClass = "h-7 w-7 p-0 rounded-md transition-colors bg-transparent hover:bg-transparent";
  const iconSize = "w-3.5 h-3.5";
  // All buttons: gray-400 default, primaryColor on hover
  const defaultColor = '#9ca3af'; // gray-400
  const hoverColor = primaryColor;

  return (
    <TooltipProvider delayDuration={300}>
      <div className={`flex items-center gap-0.5 mt-0.5 ${role === 'user' ? 'justify-end mr-9' : 'ml-9'}`}>
        {/* Copy Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              className={buttonBaseClass}
              style={{ color: defaultColor }}
              onMouseEnter={(e) => e.currentTarget.style.color = hoverColor}
              onMouseLeave={(e) => e.currentTarget.style.color = defaultColor}
            >
              {copied ? (
                <Check className={`${iconSize} text-green-500`} />
              ) : (
                <Copy className={iconSize} />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="bg-gray-900 text-white text-xs px-2 py-1">
            {copied ? 'Copied!' : 'Copy'}
          </TooltipContent>
        </Tooltip>

        {/* Regenerate Button (assistant only) */}
        {role === 'assistant' && onRegenerate && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRegenerate}
                disabled={isStreaming}
                className={buttonBaseClass}
                style={{ color: defaultColor }}
                onMouseEnter={(e) => e.currentTarget.style.color = hoverColor}
                onMouseLeave={(e) => e.currentTarget.style.color = defaultColor}
              >
                <RefreshCw className={iconSize} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="bg-gray-900 text-white text-xs px-2 py-1">
              Regenerate
            </TooltipContent>
          </Tooltip>
        )}

        {/* Edit Button (user only) */}
        {role === 'user' && onEdit && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleEdit}
                disabled={isStreaming}
                className={buttonBaseClass}
                style={{ color: defaultColor }}
                onMouseEnter={(e) => e.currentTarget.style.color = hoverColor}
                onMouseLeave={(e) => e.currentTarget.style.color = defaultColor}
              >
                <Pencil className={iconSize} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="bg-gray-900 text-white text-xs px-2 py-1">
              Edit
            </TooltipContent>
          </Tooltip>
        )}

        {/* Test Audience Button (assistant only, conditional) */}
        {role === 'assistant' && showTestAudience && onTestAudience && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleTestAudience}
                disabled={isStreaming}
                className={buttonBaseClass}
                style={{ color: defaultColor }}
                onMouseEnter={(e) => e.currentTarget.style.color = hoverColor}
                onMouseLeave={(e) => e.currentTarget.style.color = defaultColor}
              >
                <BarChart3 className={iconSize} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="bg-gray-900 text-white text-xs px-2 py-1">
              Test with Audience
            </TooltipContent>
          </Tooltip>
        )}

        {/* Branch Button (available on both user and assistant messages) */}
        {onBranch && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBranch}
                disabled={isStreaming}
                className={buttonBaseClass}
                style={{ color: defaultColor }}
                onMouseEnter={(e) => e.currentTarget.style.color = hoverColor}
                onMouseLeave={(e) => e.currentTarget.style.color = defaultColor}
              >
                <GitBranch className={iconSize} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="bg-gray-900 text-white text-xs px-2 py-1">
              Branch from here
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
};

export default MessageActions;

